<div class="max-w-7xl">
  <!-- Row 1: Video + Contact side by side -->
  <div class="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
    <!-- Introduction Video Section (2/3) -->
    <section
      class="rounded-lg border border-gray-200 bg-white p-6 lg:col-span-2"
    >
      <div class="mb-4 flex items-center gap-3">
        <div
          class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50"
        >
          <i class="pi pi-play text-xl text-primary-500"></i>
        </div>
        <h2 class="text-heading-3 font-semibold text-greyscale-950">
          {{ "help.videoSection.title" | transloco }}
        </h2>
      </div>

      <p class="mb-4 text-paragraph-reg text-greyscale-600">
        {{ "help.videoSection.description" | transloco }}
      </p>

      <div class="relative">
        <iframe
          [src]="videoUrl"
          frameborder="0"
          allowfullscreen
          class="aspect-video w-full rounded-lg border border-greyscale-200 shadow-sm"
          title="Video d'introduction Greentripper"
        >
        </iframe>
      </div>
    </section>

    <!-- Contact Support Section (1/3) -->
    <section
      class="flex flex-col justify-between rounded-lg border border-gray-200 bg-white p-6"
    >
      <div>
        <div class="mb-3 flex items-center gap-3">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50"
          >
            <lucide-icon
              name="help-circle"
              class="text-primary-500"
              [size]="22"
            />
          </div>
          <h2 class="text-heading-3 font-semibold text-greyscale-950">
            {{ "help.contact.title" | transloco }}
          </h2>
        </div>

        <p class="mb-4 text-sm text-greyscale-600">
          {{ "help.contact.intro" | transloco }}
        </p>

        <div class="mb-4 h-px bg-greyscale-200"></div>

        <div class="flex flex-col gap-4">
          <!-- Hours -->
          <div class="flex items-center gap-3">
            <div
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50"
            >
              <lucide-icon name="timer" class="text-primary-500" [size]="14" />
            </div>
            <div>
              <p class="text-sm font-medium text-greyscale-800">
                {{ "help.contact.availability" | transloco }}
              </p>
              <p class="text-xs text-greyscale-500">
                {{ "help.contact.hours" | transloco }}
              </p>
            </div>
          </div>

          <!-- Office -->
          <div class="flex items-center gap-3">
            <div
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50"
            >
              <lucide-icon
                name="map-pin"
                class="text-primary-500"
                [size]="14"
              />
            </div>
            <div>
              <p class="text-sm font-medium text-greyscale-800">
                {{ "help.contact.office" | transloco }}
              </p>
              <p class="text-xs text-greyscale-500">
                Rue d'Accolay 15/17, 1000 Bruxelles
              </p>
            </div>
          </div>

          <!-- Postal address -->
          <div class="flex items-center gap-3">
            <div
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50"
            >
              <i
                class="pi pi-inbox text-primary-500"
                style="font-size: 14px"
              ></i>
            </div>
            <div>
              <p class="text-sm font-medium text-greyscale-800">
                {{ "help.contact.postal" | transloco }}
              </p>
              <p class="text-xs text-greyscale-500">
                Cantersteen 47, 1000 Bruxelles
              </p>
            </div>
          </div>

          <!-- Languages -->
          <div class="flex items-center gap-3">
            <div
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50"
            >
              <lucide-icon name="map" class="text-primary-500" [size]="14" />
            </div>
            <div>
              <p class="text-sm font-medium text-greyscale-800">
                {{ "help.contact.languages" | transloco }}
              </p>
              <p class="text-xs text-greyscale-500">FR · EN · NL</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 flex flex-col gap-3">
        <a
          [href]="'mailto:' + supportEmail"
          class="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-primary-500 bg-primary-500 px-4 py-3 font-medium text-white transition-colors hover:bg-primary-700"
        >
          <i class="pi pi-envelope"></i>
          {{ supportEmail }}
        </a>
      </div>
    </section>
  </div>

  <!-- Contact Form Section -->
  <section class="mb-4 rounded-lg border border-gray-200 bg-white p-6">
    <div class="mb-4 flex items-center gap-3">
      <div
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50"
      >
        <lucide-icon name="mail" class="text-primary-500" [size]="22" />
      </div>
      <h2 class="text-heading-3 font-semibold text-greyscale-950">
        {{ "help.contactForm.title" | transloco }}
      </h2>
    </div>

    <p class="mb-6 text-paragraph-reg text-greyscale-600">
      {{ "help.contactForm.description" | transloco }}
    </p>

    @if (submitSuccess()) {
      <div
        class="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800"
      >
        <div class="flex items-center gap-2">
          <i class="pi pi-check-circle"></i>
          <span>{{ "help.contactForm.success" | transloco }}</span>
        </div>
      </div>
    }

    @if (submitError()) {
      <div
        class="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800"
      >
        <div class="flex items-center gap-2">
          <i class="pi pi-times-circle"></i>
          <span>{{ "help.contactForm.error" | transloco }}</span>
        </div>
      </div>
    }

    <form
      [formGroup]="contactForm"
      (ngSubmit)="submitContactForm()"
      class="grid grid-cols-1 gap-4 md:grid-cols-2"
    >
      <!-- Name -->
      <div class="flex flex-col gap-2">
        <label
          for="contact-name"
          class="text-sm font-medium text-greyscale-800"
        >
          {{ "help.contactForm.name" | transloco }} *
        </label>
        <input
          pInputText
          id="contact-name"
          formControlName="name"
          [placeholder]="'help.contactForm.namePlaceholder' | transloco"
          class="w-full"
        />
        @if (
          contactForm.get("name")?.touched &&
          contactForm.get("name")?.errors?.["required"]
        ) {
          <small class="text-red-500">{{
            "help.contactForm.nameRequired" | transloco
          }}</small>
        }
      </div>

      <!-- Email -->
      <div class="flex flex-col gap-2">
        <label
          for="contact-email"
          class="text-sm font-medium text-greyscale-800"
        >
          {{ "help.contactForm.email" | transloco }} *
        </label>
        <input
          pInputText
          id="contact-email"
          formControlName="email"
          type="email"
          [placeholder]="'help.contactForm.emailPlaceholder' | transloco"
          class="w-full"
        />
        @if (
          contactForm.get("email")?.touched &&
          contactForm.get("email")?.errors?.["required"]
        ) {
          <small class="text-red-500">{{
            "help.contactForm.emailRequired" | transloco
          }}</small>
        }
        @if (
          contactForm.get("email")?.touched &&
          contactForm.get("email")?.errors?.["email"]
        ) {
          <small class="text-red-500">{{
            "help.contactForm.emailInvalid" | transloco
          }}</small>
        }
      </div>

      <!-- Subject -->
      <div class="flex flex-col gap-2 md:col-span-2">
        <label
          for="contact-subject"
          class="text-sm font-medium text-greyscale-800"
        >
          {{ "help.contactForm.subject" | transloco }} *
        </label>
        <input
          pInputText
          id="contact-subject"
          formControlName="subject"
          [placeholder]="'help.contactForm.subjectPlaceholder' | transloco"
          class="w-full"
        />
        @if (
          contactForm.get("subject")?.touched &&
          contactForm.get("subject")?.errors?.["required"]
        ) {
          <small class="text-red-500">{{
            "help.contactForm.subjectRequired" | transloco
          }}</small>
        }
      </div>

      <!-- Message -->
      <div class="flex flex-col gap-2 md:col-span-2">
        <label
          for="contact-message"
          class="text-sm font-medium text-greyscale-800"
        >
          {{ "help.contactForm.message" | transloco }} *
        </label>
        <textarea
          pTextarea
          id="contact-message"
          formControlName="message"
          [placeholder]="'help.contactForm.messagePlaceholder' | transloco"
          rows="4"
          class="w-full"
        ></textarea>
        @if (
          contactForm.get("message")?.touched &&
          contactForm.get("message")?.errors?.["required"]
        ) {
          <small class="text-red-500">{{
            "help.contactForm.messageRequired" | transloco
          }}</small>
        }
        @if (
          contactForm.get("message")?.touched &&
          contactForm.get("message")?.errors?.["minlength"]
        ) {
          <small class="text-red-500">{{
            "help.contactForm.messageMinLength" | transloco
          }}</small>
        }
      </div>

      <!-- Submit -->
      <div class="md:col-span-2">
        <p-button
          type="submit"
          [label]="'help.contactForm.submit' | transloco"
          [loading]="isSubmitting()"
          [disabled]="isSubmitting()"
          icon="pi pi-send"
        />
      </div>
    </form>
  </section>

  <!-- Update Notes Section -->
  <section class="mb-4 rounded-lg border border-gray-200 bg-white p-6">
    <div
      class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
    >
      <div class="flex items-start gap-3">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50"
        >
          <lucide-icon name="megaphone" class="text-primary-500" [size]="22" />
        </div>
        <div>
          <h2 class="text-heading-3 font-semibold text-greyscale-950">
            {{ "help.updateNotes.title" | transloco }}
          </h2>
          <p class="mt-1 text-paragraph-reg text-greyscale-600">
            {{ "help.updateNotes.description" | transloco }}
          </p>
        </div>
      </div>

      <p-button
        [label]="'help.updateNotes.cta' | transloco"
        icon="pi pi-arrow-right"
        iconPos="right"
        severity="secondary"
        [outlined]="true"
        routerLink="/home/updates"
        styleClass="shrink-0"
      />
    </div>
  </section>

  <!-- FAQ Section -->
  <section class="rounded-lg border border-gray-200 bg-white p-6">
    <div class="mb-4 flex items-center gap-3">
      <div
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50"
      >
        <lucide-icon name="help-circle" class="text-primary-500" [size]="22" />
      </div>
      <h2 class="text-heading-3 font-semibold text-greyscale-950">
        {{ "help.faq.title" | transloco }}
      </h2>
    </div>

    <p class="mb-6 text-paragraph-reg text-greyscale-600">
      {{ "help.faq.description" | transloco }}
    </p>

    <!-- Clickable category grid -->
    <div class="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
      @for (category of faqCategories; track category.id) {
        <button
          (click)="selectCategory(category.id)"
          [class]="
            'flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all ' +
            (selectedCategoryId() === category.id
              ? 'border-primary-500 bg-primary-50'
              : 'border-greyscale-200 bg-white hover:border-primary-300 hover:bg-greyscale-50')
          "
        >
          <div
            [class]="
              'flex h-12 w-12 items-center justify-center rounded-full ' +
              (selectedCategoryId() === category.id
                ? 'bg-primary-500'
                : 'bg-primary-100')
            "
          >
            @if (category.lucideIcon) {
              <lucide-icon
                [name]="category.lucideIcon"
                [class]="
                  selectedCategoryId() === category.id
                    ? 'text-white'
                    : 'text-primary-600'
                "
                [size]="24"
              />
            } @else {
              <i
                [class]="
                  category.icon +
                  ' text-xl' +
                  (selectedCategoryId() === category.id
                    ? 'text-white'
                    : 'text-primary-600')
                "
              ></i>
            }
          </div>
          <span
            [class]="
              'text-center text-sm font-medium ' +
              (selectedCategoryId() === category.id
                ? 'text-primary-700'
                : 'text-greyscale-700')
            "
          >
            {{ category.titleKey | transloco }}
          </span>
        </button>
      }
    </div>

    <!-- Questions accordion for selected category -->
    <div class="border-t border-greyscale-200 pt-6">
      <div class="mb-4 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div
            class="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100"
          >
            @if (selectedCategory().lucideIcon) {
              <lucide-icon
                [name]="selectedCategory().lucideIcon!"
                class="text-primary-600"
                [size]="16"
              />
            } @else {
              <i
                [class]="selectedCategory().icon + ' text-sm text-primary-600'"
              ></i>
            }
          </div>
          <h3 class="text-heading-4 font-semibold text-greyscale-900">
            {{ selectedCategory().titleKey | transloco }}
          </h3>
        </div>
        <button
          (click)="toggleAllPanels()"
          class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
        >
          <i
            [class]="allPanelsExpanded ? 'pi pi-minus' : 'pi pi-plus'"
            style="font-size: 12px"
          ></i>
          {{
            allPanelsExpanded
              ? ("help.faq.collapseAll" | transloco)
              : ("help.faq.expandAll" | transloco)
          }}
        </button>
      </div>

      <p-accordion [multiple]="true" [(value)]="expandedPanels">
        @for (
          question of selectedCategory().questions;
          track question.id;
          let i = $index
        ) {
          <p-accordion-panel [value]="selectedCategory().id + '-' + i">
            <p-accordion-header>
              <span class="text-paragraph-reg font-medium text-greyscale-800">
                {{ question.questionKey | transloco }}
              </span>
            </p-accordion-header>
            <p-accordion-content>
              <p class="m-0 py-2 text-paragraph-reg text-greyscale-600">
                {{ question.answerKey | transloco }}
              </p>
            </p-accordion-content>
          </p-accordion-panel>
        }
      </p-accordion>
    </div>
  </section>
</div>
